import { useEffect, useRef, useState } from "react";
import type { Design } from "../App";
import { rasterizePdfFirstPage } from "../lib/pdfRasterize";
import VoiceInputButton from "./VoiceInputButton";
import type { VoiceInputButtonHandle } from "./VoiceInputButton";

type ChatPanelProps = {
  design: Design;
  designSummary: string;
  onPatch: (p: Partial<Design>) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};
type Role = "user" | "assistant";
type AppliedPatch = { tool: string; args: Record<string, unknown>; applied: Partial<Design> };
type ToolError = { tool: string; args: Record<string, unknown>; error: string };
type Message = {
  role: Role;
  content: string;
  patches?: AppliedPatch[];
  toolErrors?: ToolError[];
  imagePreview?: string;
};
type ChatResponse = { assistantText: string; patches: AppliedPatch[]; toolErrors?: ToolError[] };
type Attachment = { dataUrl: string; name: string; kind: "image" | "pdf" };

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB raw upload cap
const ACCEPT = "image/png,image/jpeg,image/webp,application/pdf";

const sheetBase =
  "border border-neutral-200/80 bg-white/95 shadow-[0_-8px_30px_rgba(15,23,42,0.08)] backdrop-blur supports-[backdrop-filter]:bg-white/85";

const SUGGESTIONS = ["Make it 10′ × 12′", "Add stairs", "Make rails white"];

const SVG_PROPS = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
} as const;

const SparkleIcon = ({ className = "size-4" }: { className?: string }) => (
  <svg {...SVG_PROPS} strokeWidth={1.8} className={className}>
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const CloseIcon = ({ className = "size-4" }: { className?: string }) => (
  <svg {...SVG_PROPS} strokeWidth={2} className={className}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const SendIcon = ({ className = "size-4" }: { className?: string }) => (
  <svg {...SVG_PROPS} strokeWidth={2} className={className}>
    <path d="M5 12l14-7-5 14-3-6-6-1z" />
  </svg>
);

const PaperclipIcon = ({ className = "size-4" }: { className?: string }) => (
  <svg {...SVG_PROPS} strokeWidth={2} className={className}>
    <path d="M21 11.5l-8.5 8.5a5.5 5.5 0 0 1-7.78-7.78l9.19-9.19a3.75 3.75 0 0 1 5.3 5.3l-9.19 9.19a2 2 0 0 1-2.83-2.83l8.49-8.49" />
  </svg>
);

function summarizePatch(patch: AppliedPatch): string {
  const { tool } = patch;
  const a = (patch.args ?? {}) as Record<string, unknown>;
  if (tool === "set_deck_dimensions") {
    return `deck ${a.length_ft}'×${a.width_ft}'`;
  }
  if (tool === "set_polygon_vertices") return "shape updated";
  if (tool === "set_rail_color") return `rails ${a.color}`;
  if (tool === "set_rail_height") return `rails ${a.inches}″`;
  if (tool === "set_stairs") {
    const parts: string[] = [];
    if (typeof a.enabled === "boolean") parts.push(a.enabled ? "on" : "off");
    if (typeof a.length_ft === "number") parts.push(`${a.length_ft}'`);
    if (typeof a.edge_index === "number") parts.push(`side ${a.edge_index + 1}`);
    if (typeof a.with_railing === "boolean") {
      parts.push(`railing ${a.with_railing ? "on" : "off"}`);
    }
    return parts.length > 0 ? `stairs ${parts.join(" ")}` : "stairs";
  }
  if (tool === "reset_deck") return "reset";
  if (tool === "set_edge_railing") {
    const idx = typeof a.edge_index === "number" ? a.edge_index + 1 : "?";
    return `side ${idx} ${a.railed === false ? "no rail" : "rail on"}`;
  }
  return tool.replaceAll("_", " ");
}

export default function ChatPanel({
  design,
  designSummary,
  onPatch,
  open,
  onOpenChange,
}: ChatPanelProps) {
  const setOpen = onOpenChange;
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [attaching, setAttaching] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const voiceInputRef = useRef<VoiceInputButtonHandle | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(id);
  }, [open]);

  function scrollToBottom() {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }

  // Force-scroll on message arrival / typing state. Repeat over a short
  // window so streaming text and late-laying-out images can't leave us short.
  useEffect(() => {
    if (!open) return;
    scrollToBottom();
    const frames: number[] = [];
    const schedule = (n: number) => {
      if (n === 0) return;
      frames.push(
        requestAnimationFrame(() => {
          scrollToBottom();
          schedule(n - 1);
        }),
      );
    };
    schedule(3);
    const t = window.setTimeout(scrollToBottom, 120);
    return () => {
      frames.forEach(cancelAnimationFrame);
      window.clearTimeout(t);
    };
  }, [messages, pending, attachment, error, open]);

  useEffect(() => {
    if (!open) return;
    const content = contentRef.current;
    if (!content) return;
    const ro = new ResizeObserver(() => scrollToBottom());
    ro.observe(content);
    return () => ro.disconnect();
  }, [open]);

  async function send(text: string) {
    const trimmed = text.trim();
    if (pending) return;
    if (!trimmed && !attachment) return;
    voiceInputRef.current?.stop();
    const messageText =
      trimmed || (attachment ? "Build this deck from the image." : "");
    const userMsg: Message = {
      role: "user",
      content: messageText,
      imagePreview: attachment?.dataUrl,
    };
    const next = [...messages, userMsg];
    const sentAttachment = attachment;
    setMessages(next);
    setInput("");
    setAttachment(null);
    setPending(true);
    setError(null);
    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: next.map((m) => ({ role: m.role, content: m.content })),
          design,
          designSummary,
          ...(sentAttachment ? { image: sentAttachment.dataUrl } : {}),
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as ChatResponse;
      const appliedPatches = (data.patches ?? []).filter((p) => p && p.applied);
      if (appliedPatches.length > 0) {
        const merged = appliedPatches.reduce<Partial<Design>>(
          (acc, p) => ({ ...acc, ...p.applied }),
          {},
        );
        onPatch(merged);
      }
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.assistantText ?? "",
          patches: data.patches ?? [],
          toolErrors: data.toolErrors ?? [],
        },
      ]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "unknown error";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Sorry, couldn’t apply that. (${msg})` },
      ]);
      setError(msg);
    } finally {
      setPending(false);
    }
  }

  async function handleFile(file: File) {
    if (file.size > MAX_FILE_BYTES) {
      setError(`File is too large (max ${Math.round(MAX_FILE_BYTES / 1024 / 1024)} MB).`);
      return;
    }
    setError(null);
    setAttaching(true);
    try {
      if (file.type === "application/pdf") {
        const dataUrl = await rasterizePdfFirstPage(file);
        setAttachment({ dataUrl, name: file.name, kind: "pdf" });
      } else if (file.type.startsWith("image/")) {
        const dataUrl = await readAsDataURL(file);
        setAttachment({ dataUrl, name: file.name, kind: "image" });
      } else {
        setError("Upload a PNG, JPG, WEBP, or PDF.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn’t read that file.");
    } finally {
      setAttaching(false);
    }
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) void handleFile(f);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      void send(input);
    }
  }

  const fab = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-label="Open designer chat"
      className="grid size-11 place-items-center rounded-full bg-[var(--color-brand)] text-white shadow-[0_4px_14px_rgba(15,23,42,0.18)] transition hover:bg-[var(--color-brand-dark)]"
    >
      <svg {...SVG_PROPS} strokeWidth={2} className="size-5">
        <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.4A8 8 0 1 1 21 12Z" />
      </svg>
    </button>
  );

  const header = (
    <div className="flex items-center justify-between border-b border-neutral-200/80 px-4 py-2.5">
      <div className="flex items-center gap-2">
        <span className="grid size-6 place-items-center rounded-full bg-[var(--color-brand)] text-white">
          <SparkleIcon className="size-3.5" />
        </span>
        <h2 className="text-sm font-semibold text-neutral-900">Designer chat</h2>
      </div>
      <button
        type="button"
        aria-label="Close chat"
        onClick={() => setOpen(false)}
        className="grid size-8 place-items-center rounded-full text-neutral-500 transition hover:bg-neutral-100"
      >
        <CloseIcon />
      </button>
    </div>
  );

  const messageList = (
    <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3" aria-live="polite">
      <div ref={contentRef}>
        {messages.length === 0 && !pending && (
          <div className="flex flex-col gap-3 text-sm text-neutral-600">
            <p className="text-neutral-500">Try one of these, or type your own:</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setInput(s);
                    inputRef.current?.focus();
                  }}
                  className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs text-neutral-700 transition hover:border-neutral-300 hover:bg-neutral-50"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-col gap-3">
          {messages.map((m, i) => (
            <MessageBubble key={i} message={m} onLoaded={scrollToBottom} />
          ))}
          {pending && <TypingIndicator />}
        </div>
      </div>
    </div>
  );

  const canSend = !pending && (input.trim().length > 0 || attachment !== null);

  const inputBar = (
    <div className="border-t border-neutral-200/80 bg-white/80 px-3 py-2.5">
      {error && (
        <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-700">
          {error}
        </div>
      )}
      {attachment && (
        <div className="mb-2 flex items-center gap-2 rounded-2xl border border-neutral-200 bg-white px-2 py-1.5">
          <img
            src={attachment.dataUrl}
            alt={attachment.name}
            className="size-12 rounded-lg border border-neutral-200 object-cover"
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-medium text-neutral-800">
              {attachment.name}
            </div>
            <div className="text-[10px] uppercase tracking-wide text-neutral-400">
              {attachment.kind === "pdf" ? "PDF · page 1" : "Image"}
            </div>
          </div>
          <button
            type="button"
            aria-label="Remove attachment"
            onClick={() => setAttachment(null)}
            className="grid size-7 shrink-0 place-items-center rounded-full text-neutral-500 transition hover:bg-neutral-100"
          >
            <CloseIcon className="size-3.5" />
          </button>
        </div>
      )}
      {attaching && !attachment && (
        <div className="mb-2 rounded-lg border border-neutral-200 bg-neutral-50 px-2.5 py-1.5 text-xs text-neutral-600">
          Reading file…
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        name="chat-attachment"
        accept={ACCEPT}
        className="hidden"
        onChange={onPickFile}
      />
      <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm transition focus-within:border-[var(--color-brand)] focus-within:ring-2 focus-within:ring-[var(--color-brand)]/15">
        <textarea
          ref={inputRef}
          name="designer-chat-message"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          aria-label="Chat with the designer"
          placeholder={
            attachment ? "Add a note (optional)…" : "What do you want to change?"
          }
          className="block max-h-40 min-h-[2.75rem] w-full resize-none rounded-t-2xl bg-transparent px-3 pt-2.5 pb-1 text-sm text-neutral-900 placeholder:text-neutral-500 focus:outline-none"
        />
        <div className="flex items-center justify-end gap-1 px-2 pb-1.5">
          <VoiceInputButton
            ref={voiceInputRef}
            value={input}
            onChange={setInput}
            disabled={pending || attaching}
            inputRef={inputRef}
          />
          <button
            type="button"
            aria-label="Attach image or PDF"
            disabled={pending || attaching}
            onClick={() => fileInputRef.current?.click()}
            className="grid size-8 place-items-center rounded-full text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <PaperclipIcon className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Send"
            disabled={!canSend}
            onClick={() => void send(input)}
            className="grid size-8 place-items-center rounded-full bg-[var(--color-brand)] text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <SendIcon className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {!open && (
        <div className="absolute bottom-5 left-5 z-30 hidden sm:block">{fab}</div>
      )}
      {!open && (
        <div className="absolute bottom-[5.25rem] left-3 z-30 sm:hidden">{fab}</div>
      )}
      {open && (
        <div
          className="absolute inset-y-0 left-0 z-40 hidden sm:flex w-[360px] flex-col overflow-hidden border-r border-neutral-200/80 bg-white/95 shadow-[8px_0_30px_rgba(15,23,42,0.08)] backdrop-blur supports-[backdrop-filter]:bg-white/85"
        >
          {header}
          {messageList}
          {inputBar}
        </div>
      )}
      {open && (
        <div className="sm:hidden">
          <button
            type="button"
            aria-label="Close chat"
            onClick={() => setOpen(false)}
            className="absolute inset-0 z-40 bg-black/30 backdrop-blur-[2px]"
          />
          <div
            className={`absolute inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col overflow-hidden rounded-t-3xl ${sheetBase}`}
            style={{ height: "78vh" }}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="self-center py-1.5"
            >
              <span className="block h-1 w-10 rounded-full bg-neutral-300" />
            </button>
            {header}
            {messageList}
            {inputBar}
          </div>
        </div>
      )}
    </>
  );
}

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("Couldn’t read that file."));
    r.onload = () => {
      const v = r.result;
      if (typeof v === "string") resolve(v);
      else reject(new Error("Couldn’t read that file."));
    };
    r.readAsDataURL(file);
  });
}

function MessageBubble({
  message,
  onLoaded,
}: {
  message: Message;
  onLoaded?: () => void;
}) {
  const isUser = message.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className="flex max-w-[85%] flex-col gap-1.5">
        {isUser && message.imagePreview && (
          <img
            src={message.imagePreview}
            alt="Uploaded sketch"
            onLoad={onLoaded}
            className="self-end max-h-48 w-auto rounded-2xl rounded-br-md border border-neutral-200 object-contain"
          />
        )}
        {message.content && (
          <div
            className={
              "rounded-2xl px-3 py-2 text-sm leading-snug " +
              (isUser
                ? "whitespace-pre-wrap rounded-br-md bg-[var(--color-brand)] text-white"
                : "rounded-bl-md bg-neutral-100 text-neutral-900")
            }
          >
            {isUser ? message.content : renderMarkdown(message.content, true)}
          </div>
        )}
        {!isUser && message.patches && message.patches.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {message.patches.map((p, i) => (
              <span
                key={i}
                className="rounded-full border border-neutral-200 bg-white/80 px-2 py-0.5 text-[10px] font-medium text-neutral-600"
              >
                {summarizePatch(p)}
              </span>
            ))}
          </div>
        )}
        {!isUser && message.toolErrors && message.toolErrors.length > 0 && (
          <div className="flex flex-col gap-0.5 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
            {message.toolErrors.map((te, i) => (
              <div key={i}>
                <span className="font-medium">{te.tool}</span>: {te.error}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="flex items-center gap-1 rounded-2xl rounded-bl-md bg-neutral-100 px-3 py-2.5">
        {["0ms", "150ms", "300ms"].map((d) => (
          <span
            key={d}
            className="block size-1.5 animate-bounce rounded-full bg-neutral-400"
            style={{ animationDelay: d }}
          />
        ))}
      </div>
    </div>
  );
}

const INLINE_RE =
  /(`[^`\n]+`)|(\*\*[^*\n]+\*\*)|(\*[^*\n]+\*)|(\[[^\]\n]+\]\([^)\s]+\))/g;

function renderInline(text: string, baseKey: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const t = m[0];
    const k = `${baseKey}-${key++}`;
    if (t.startsWith("`")) {
      out.push(
        <code
          key={k}
          className="rounded bg-black/10 px-1 font-mono text-[0.85em]"
        >
          {t.slice(1, -1)}
        </code>,
      );
    } else if (t.startsWith("**")) {
      out.push(<strong key={k}>{t.slice(2, -2)}</strong>);
    } else if (t.startsWith("*")) {
      out.push(<em key={k}>{t.slice(1, -1)}</em>);
    } else {
      const linkM = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(t);
      if (linkM) {
        out.push(
          <a
            key={k}
            href={linkM[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            {linkM[1]}
          </a>,
        );
      } else {
        out.push(t);
      }
    }
    last = INLINE_RE.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Split a paragraph into reveal "steps" — sentences and hard-newline lines.
// Each step animates in separately so long paragraphs don't pop as one wall.
function splitSteps(text: string): string[] {
  const out: string[] = [];
  for (const line of text.split("\n")) {
    if (line.trim() === "") continue;
    const parts = line.split(/(?<=[.!?])\s+(?=[A-Z(\["'])/);
    for (const p of parts) if (p.trim() !== "") out.push(p);
  }
  return out;
}

const STEP_MS = 55;
const STEP_CAP_MS = 1400;
const stepDelay = (i: number) => `${Math.min(i * STEP_MS, STEP_CAP_MS)}ms`;

function renderMarkdown(content: string, animate: boolean): React.ReactNode {
  const lines = content.split("\n");
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;
  let step = 0;
  const ulRe = /^\s*[-*]\s+(.*)$/;
  const olRe = /^\s*(\d+)\.\s+(.*)$/;
  while (i < lines.length) {
    const line = lines[i];
    const ulM = ulRe.exec(line);
    const olM = olRe.exec(line);
    if (ulM || olM) {
      const ordered = !!olM;
      const items: string[] = [];
      while (i < lines.length) {
        const u = ulRe.exec(lines[i]);
        const o = olRe.exec(lines[i]);
        if (ordered && o) {
          items.push(o[2]);
          i++;
        } else if (!ordered && u) {
          items.push(u[1]);
          i++;
        } else break;
      }
      const k = `b${key++}`;
      const renderItems = items.map((it, idx) => {
        const s = step++;
        return (
          <li
            key={idx}
            className={animate ? "chat-reveal" : undefined}
            style={animate ? { animationDelay: stepDelay(s) } : undefined}
          >
            {renderInline(it, `${k}-${idx}`)}
          </li>
        );
      });
      blocks.push(
        ordered ? (
          <ol key={k} className="my-1 list-decimal space-y-0.5 pl-5">
            {renderItems}
          </ol>
        ) : (
          <ul key={k} className="my-1 list-disc space-y-0.5 pl-5">
            {renderItems}
          </ul>
        ),
      );
    } else {
      const paraLines: string[] = [];
      while (i < lines.length) {
        if (ulRe.test(lines[i]) || olRe.test(lines[i])) break;
        paraLines.push(lines[i]);
        i++;
      }
      const text = paraLines.join("\n").replace(/\n+$/g, "");
      if (text.trim() !== "") {
        const k = `b${key++}`;
        if (animate) {
          const segs = splitSteps(text);
          blocks.push(
            <p key={k} className="whitespace-pre-wrap">
              {segs.map((seg, idx) => {
                const s = step++;
                return (
                  <span
                    key={idx}
                    className="chat-reveal"
                    style={{ animationDelay: stepDelay(s) }}
                  >
                    {renderInline(seg, `${k}-${idx}`)}
                    {idx < segs.length - 1 ? " " : ""}
                  </span>
                );
              })}
            </p>,
          );
        } else {
          blocks.push(
            <p key={k} className="whitespace-pre-wrap">
              {renderInline(text, k)}
            </p>,
          );
        }
      }
    }
  }
  return <>{blocks}</>;
}
